/* eslint-disable react/prop-types */
import React, { useState, useCallback } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import { q, num, Kpi } from './kit';
import { formatUTCDate } from '../../../utils/utils';
import SleepSessionsModal from '../SleepSessionsModal';

const hm = (mins) => {
  const m = Number(mins);
  if (!Number.isFinite(m) || m <= 0) return '—';
  return `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
};

// The sleep endpoint formats the day from date_from treating it as epoch SECONDS, but
// some rows store MILLISECONDS → dates land in the year ~56,000. Recompute the day from
// the raw time_from epoch, detecting the unit, so the axis shows real dates.
const dayFromEpoch = (epoch, fallback) => {
  const n = Number(epoch);
  if (!Number.isFinite(n) || n <= 0) return fallback || null;
  const ms = n > 1e11 ? n : n * 1000; // > ~1e11 ⇒ already milliseconds
  const d = new Date(ms);
  const yr = d.getUTCFullYear();
  if (Number.isNaN(yr) || yr < 2000 || yr > 2100) return fallback || null;
  return d.toISOString().slice(0, 10);
};

const SleepTab = ({ userId, from, to, authGet }) => {
  // Per-day session drill-down (the clickable "Sessions" count).
  const [drill, setDrill] = useState(null); // { date, loading, error, sessions }

  const openSessions = useCallback(async (row) => {
    const date = row.apiDate || row.date;
    setDrill({ date: row.date || date, loading: true, error: false, sessions: [] });
    try {
      const res = await authGet(`/admin/api/user-sleep-sessions-by-date?${q(userId, null, null, { date })}`);
      setDrill({ date: row.date || date, loading: false, error: false, sessions: res.data.sessions || [] });
    } catch (err) {
      setDrill({ date: row.date || date, loading: false, error: true, sessions: [] });
    }
  }, [authGet, userId]);

  const { status, data, reload } = useTabData(async () => {
    // Sleep detail returns chart_data / summary / user at the TOP LEVEL (not under data).
    const res = await authGet(`/admin/api/user-sleep-summary-details?${q(userId, from, to)}`);
    return { chart: res.data.chart_data || [], summary: res.data.summary || {} };
  }, [userId, from, to]);

  if (status === 'loading') return <TabLoading />;
  if (status === 'error') return <TabError onRetry={reload} />;

  const { chart, summary } = data;
  if (!chart.length && !(Number(summary.total_sessions) > 0)) {
    return <TabEmpty title="No sleep data" message="This user has no sleep sessions in the selected window." icon="☾" />;
  }
  const bars = chart
    // `apiDate` is the day bucket the endpoint grouped on — the drill-down must ask
    // with THAT value, not the client-derived one, or the two can disagree by a day.
    .map((d) => ({
      date: dayFromEpoch(d.time_from, null),
      apiDate: d.date,
      minutes: Number(d.total_sleep_minutes) || 0,
      sessions: d.uuid_count,
    }))
    .filter((d) => d.date)
    .sort((a, b) => a.date.localeCompare(b.date));
  // Table mirrors the standalone /user-sleep-summary-details page (most-recent-first).
  const rows = [...bars].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div data-testid="ud-sleep">
      <div className="ud-kpis ud-kpis--2">
        <Kpi label="Sleep sessions" value={num(summary.total_sessions, 0)} />
        <Kpi label="Avg sleep" value={hm(summary.avg_sleep_minutes)} />
      </div>

      <div className="au-panel">
        <div className="au-panel__head"><h4 className="au-panel__title">Sleep minutes per day</h4></div>
        <div style={{ padding: '12px 8px' }}>
          {bars.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={bars} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f1" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="minutes" fill="#163a5f" name="Minutes" radius={[3, 3, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="au-chart-empty">No per-day sleep detail to plot.</div>
          )}
        </div>
      </div>

      <div className="au-panel">
        <div className="au-panel__head"><h4 className="au-panel__title">Sleep by day</h4></div>
        <div className="au-table-wrap">
          <table className="au-table" data-testid="ud-sleep-table">
            <thead><tr><th>No</th><th>Date</th><th className="tnum">Total sleep</th><th className="tnum">Sessions</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.date}>
                  <td data-label="No" className="tnum">{i + 1}</td>
                  <td data-label="Date" className="tnum">{formatUTCDate(r.date)}</td>
                  <td data-label="Total sleep" className="tnum">{hm(r.minutes)}</td>
                  <td data-label="Sessions" className="tnum">
                    {Number(r.sessions) > 0 ? (
                      <button
                        type="button"
                        className="au-link ud-sess-btn"
                        data-testid={`ud-sleep-sessions-btn-${r.apiDate || r.date}`}
                        onClick={() => openSessions(r)}
                        aria-label={`View the ${r.sessions} sleep session${Number(r.sessions) === 1 ? '' : 's'} on ${formatUTCDate(r.date)}`}
                      >
                        {r.sessions}
                      </button>
                    ) : (r.sessions ?? '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SleepSessionsModal
        open={!!drill}
        date={drill?.date}
        sessions={drill?.sessions || []}
        loading={!!drill?.loading}
        error={!!drill?.error}
        onClose={() => setDrill(null)}
      />
    </div>
  );
};

export default SleepTab;
